import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Mail, User, Camera, Clock, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

export default function Datenschutz() {
  return (
    <div data-testid="datenschutz-page" className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <Link to="/">
          <Button variant="ghost" className="mb-8 text-[#b46eb6] hover:bg-[#b46eb6]/5" data-testid="datenschutz-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zur Startseite
          </Button>
        </Link>

        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-8" style={{ color: "#1F2937" }}>
          Datenschutzerklärung
        </h1>
        <div className="section-divider mb-8" />

        <div className="prose prose-lg max-w-none space-y-8" style={{ color: "#4B5563" }}>
          
          {/* 1. Allgemeines */}
          <section>
            <h2 className="font-heading text-2xl font-semibold flex items-center gap-2 mb-4" style={{ color: "#1F2937" }}>
              <Shield className="w-6 h-6" style={{ color: "#b46eb6" }} />
              1. Allgemeines
            </h2>
            <p>
              Der Schutz deiner persönlichen Daten ist uns sehr wichtig. Wir behandeln deine Daten 
              vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften sowie dieser 
              Datenschutzerklärung.
            </p>
          </section>

          {/* 2. Verantwortliche Stelle */}
          <section>
            <h2 className="font-heading text-2xl font-semibold flex items-center gap-2 mb-4" style={{ color: "#1F2937" }}>
              <User className="w-6 h-6" style={{ color: "#b46eb6" }} />
              2. Verantwortliche Stelle
            </h2>
            <p>Verantwortlich für die Datenverarbeitung ist:</p>
            <div className="bg-gray-50 rounded-xl p-6 mt-4">
              <p className="font-semibold" style={{ color: "#1F2937" }}>Zukunft durch medizinische Forschung e.V.</p>
              <p>
                E-Mail:{" "}
                <a href="mailto:info@runforrett.de" className="underline hover:no-underline" style={{ color: "#b46eb6" }}>
                  info@runforrett.de
                </a>
              </p>
            </div>
          </section>

          {/* 3. Erhebung und Speicherung */}
          <section>
            <h2 className="font-heading text-2xl font-semibold mb-4" style={{ color: "#1F2937" }}>
              3. Erhebung und Speicherung personenbezogener Daten
            </h2>
            <p>
              Wenn du dich über unsere Webseite{" "}
              <a href="https://www.runforrett.de" className="underline hover:no-underline" style={{ color: "#b46eb6" }}>
                www.runforrett.de
              </a>{" "}
              zum Spendenlauf anmeldest, speichern wir die von dir eingegebenen Daten wie:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 ml-4">
              <li>Name, Vorname</li>
              <li>E-Mail-Adresse</li>
              <li>ggf. Name des Sponsor-Unternehmens</li>
              <li>Anzahl der gelaufenen Runden (am Veranstaltungstag)</li>
            </ul>
            <p className="mt-4 p-4 rounded-xl" style={{ background: "rgba(149, 225, 187, 0.15)" }}>
              <strong>Diese Daten dienen ausschließlich der Organisation und Durchführung des Spendenlaufs. 
              Sie werden nicht an Dritte weitergegeben.</strong>
            </p>
          </section>

          {/* 4. Foto- und Videoaufnahmen */}
          <section>
            <h2 className="font-heading text-2xl font-semibold flex items-center gap-2 mb-4" style={{ color: "#1F2937" }}>
              <Camera className="w-6 h-6" style={{ color: "#b46eb6" }} />
              4. Foto- und Videoaufnahmen
            </h2>
            <p>
              Im Rahmen des Spendenlaufs werden Bild- und Videoaufnahmen gemacht, die zur 
              Öffentlichkeitsarbeit (z. B. Webseite, Social Media, Presseberichte) genutzt werden können.
            </p>
            <p className="mt-4">
              Da es sich um eine öffentliche Veranstaltung handelt, ist dies durch das 
              <strong> Kunsturhebergesetz (§ 23 KUG)</strong> grundsätzlich erlaubt.
            </p>
            <p className="mt-4 p-4 rounded-xl" style={{ background: "rgba(180, 110, 182, 0.1)" }}>
              <strong>Hinweis:</strong> Solltest du nicht abgebildet werden wollen, sprich bitte 
              vor Ort ein Mitglied des Teams an.
            </p>
          </section>

          {/* 5. Speicherdauer */}
          <section>
            <h2 className="font-heading text-2xl font-semibold flex items-center gap-2 mb-4" style={{ color: "#1F2937" }}>
              <Clock className="w-6 h-6" style={{ color: "#b46eb6" }} />
              5. Speicherdauer
            </h2>
            <p>
              Wir speichern deine Daten nur so lange, wie es für die Organisation und 
              Nachbereitung der Veranstaltung notwendig ist. 
              <strong> Spätestens nach 6 Monaten</strong> werden personenbezogene Daten 
              anonymisiert oder gelöscht.
            </p>
          </section>

          {/* 6. Deine Rechte */}
          <section>
            <h2 className="font-heading text-2xl font-semibold flex items-center gap-2 mb-4" style={{ color: "#1F2937" }}>
              <CheckCircle className="w-6 h-6" style={{ color: "#b46eb6" }} />
              6. Deine Rechte
            </h2>
            <p>Du hast jederzeit das Recht auf:</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full mt-2" style={{ background: "#b46eb6" }}></span>
                <span>Auskunft über deine gespeicherten Daten</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full mt-2" style={{ background: "#b46eb6" }}></span>
                <span>Berichtigung unrichtiger Daten</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full mt-2" style={{ background: "#b46eb6" }}></span>
                <span>Löschung oder Einschränkung der Verarbeitung</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full mt-2" style={{ background: "#b46eb6" }}></span>
                <span>Widerspruch gegen die Verarbeitung</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full mt-2" style={{ background: "#b46eb6" }}></span>
                <span>Datenübertragbarkeit (sofern anwendbar)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full mt-2" style={{ background: "#b46eb6" }}></span>
                <span>Beschwerde bei der zuständigen Aufsichtsbehörde</span>
              </li>
            </ul>
          </section>

          {/* 7. Kontakt */}
          <section>
            <h2 className="font-heading text-2xl font-semibold flex items-center gap-2 mb-4" style={{ color: "#1F2937" }}>
              <Mail className="w-6 h-6" style={{ color: "#b46eb6" }} />
              7. Kontakt bei Datenschutzfragen
            </h2>
            <p>
              Wenn du Fragen zum Datenschutz hast oder Auskunft über deine gespeicherten 
              Daten möchtest, melde dich gerne per E-Mail an:
            </p>
            <div className="mt-4 p-6 rounded-xl text-center" style={{ background: "rgba(180, 110, 182, 0.1)" }}>
              <a 
                href="mailto:info@runforrett.de" 
                className="text-xl font-semibold underline hover:no-underline" 
                style={{ color: "#b46eb6" }}
              >
                info@runforrett.de
              </a>
            </div>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap gap-4">
          <Link to="/impressum" className="text-sm underline hover:no-underline" style={{ color: "#b46eb6" }}>
            Impressum
          </Link>
        </div>
      </div>
    </div>
  );
}
